import React, { useCallback, useEffect, useState } from 'react';
import { facilityApi } from '../../api/api';
import { notify } from '../../utils/notifications';

export default function FacilityAdminPanel() {
    const [facilities, setFacilities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ id: '', name: '', facility_config_type: 'home_health' });

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            setFacilities(await facilityApi.list());
        } catch (e) {
            notify.error('Failed to load facilities');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const onCreate = async (e) => {
        e.preventDefault();
        try {
            await facilityApi.create(form);
            notify.success(`Facility ${form.id} created`);
            setForm({ id: '', name: '', facility_config_type: 'home_health' });
            setCreating(false);
            reload();
        } catch (err) {
            notify.error(err?.response?.data?.detail || 'Failed to create facility');
        }
    };

    return (
        <div className="facility-admin-panel">
            <div className="facility-admin-header">
                <h3>Facilities</h3>
                <button onClick={() => setCreating(!creating)}>
                    {creating ? 'Cancel' : '+ New facility'}
                </button>
            </div>
            {creating && (
                <form onSubmit={onCreate} className="facility-create-form">
                    <input
                        placeholder="id (slug)"
                        value={form.id}
                        onChange={(e) => setForm({ ...form, id: e.target.value })}
                        required
                    />
                    <input
                        placeholder="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                    />
                    <select
                        value={form.facility_config_type}
                        onChange={(e) => setForm({ ...form, facility_config_type: e.target.value })}
                    >
                        <option value="home_health">home_health</option>
                        <option value="ipr">ipr</option>
                        <option value="ltach">ltach</option>
                    </select>
                    <button type="submit">Create</button>
                </form>
            )}
            {loading && <div>Loading…</div>}
            {!loading && facilities.length === 0 && (
                <p className="facility-empty">No facilities yet.</p>
            )}
            {!loading && facilities.length > 0 && (
                <table className="facility-admin-table">
                    <thead>
                        <tr><th>ID</th><th>Name</th><th>Type</th><th>State</th><th /></tr>
                    </thead>
                    <tbody>
                        {facilities.map((f) => (
                            <FacilityRow key={f.id} facility={f} onChanged={reload} />
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function FacilityRow({ facility, onChanged }) {
    const [expanded, setExpanded] = useState(false);
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [assign, setAssign] = useState({ uid: '', role: 'case_manager' });

    const loadMembers = useCallback(async () => {
        setLoadingMembers(true);
        try {
            setMembers(await facilityApi.listMembers(facility.id));
        } catch {
            notify.error('Failed to load members');
        } finally {
            setLoadingMembers(false);
        }
    }, [facility.id]);

    useEffect(() => {
        if (expanded) loadMembers();
    }, [expanded, loadMembers]);

    const onAssign = async (e) => {
        e.preventDefault();
        try {
            await facilityApi.assignMember(facility.id, assign);
            setAssign({ uid: '', role: 'case_manager' });
            loadMembers();
            onChanged?.();
        } catch (err) {
            notify.error(err?.response?.data?.detail || 'Failed to assign member');
        }
    };

    const onRemove = async (uid) => {
        if (!window.confirm(`Remove member ${uid}?`)) return;
        try {
            await facilityApi.removeMember(facility.id, uid);
            loadMembers();
        } catch (err) {
            notify.error(err?.response?.data?.detail || 'Failed to remove member');
        }
    };

    return (
        <>
            <tr>
                <td>{facility.id}</td>
                <td>{facility.name}</td>
                <td>{facility.facility_config_type}</td>
                <td>{facility.state}</td>
                <td>
                    <button onClick={() => setExpanded(!expanded)}>
                        {expanded ? '−' : '+'}
                    </button>
                </td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={5}>
                        <div className="facility-members">
                            <h4>Members</h4>
                            {loadingMembers ? (
                                <p>Loading…</p>
                            ) : members.length === 0 ? (
                                <p className="facility-empty">No members assigned yet.</p>
                            ) : (
                                <ul>
                                    {members.map((m) => (
                                        <li key={m.uid}>
                                            {m.name || m.uid} ({m.email || '—'}) — {m.role}
                                            <button onClick={() => onRemove(m.uid)}>Remove</button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <form onSubmit={onAssign} className="facility-assign-form">
                                <input
                                    placeholder="user uid"
                                    value={assign.uid}
                                    onChange={(e) => setAssign({ ...assign, uid: e.target.value })}
                                    required
                                />
                                <select
                                    value={assign.role}
                                    onChange={(e) => setAssign({ ...assign, role: e.target.value })}
                                >
                                    <option value="case_manager">case_manager</option>
                                    <option value="facility_admin">facility_admin</option>
                                </select>
                                <button type="submit">Assign</button>
                            </form>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
